"use client";

import { useEffect, useId, useMemo, useState } from "react";
import { Download, ExternalLink, LineChart as LineChartIcon, Table2 } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { macroSeriesCsv, macroSourceLinks, mergeSeriesByDate } from "@/lib/macro";
import type { MacroSeries } from "@/lib/types";
import { DataState } from "./data-state";

type MacroChartProps = {
  title: string;
  description?: string;
  series: MacroSeries[];
  height?: number;
  zeroLine?: boolean;
  valueFormatter?: (value: number) => string;
  sourceSeries?: MacroSeries[];
};

const COLORS = ["#6b9dff", "#43d6a0", "#f7c05a", "#c5a5ff", "#ff8d95", "#90a4bd"];
const DASHES = [undefined, "7 4", "2 4", "10 3 2 3", "5 5", "1 3"];

type TooltipEntry = {
  key: string;
  label: string;
  unit: string;
  value: number;
  observationDate: string;
  displayDate: string;
  sourceName: string;
  status: string;
};

function tooltipEntriesForRow(row: Record<string, string | number>, series: MacroSeries[]): TooltipEntry[] {
  return series.flatMap((item) => {
    const value = row[item.key];
    if (typeof value !== "number") return [];
    return [{
      key: item.key,
      label: item.label,
      unit: item.unit,
      value,
      observationDate: String(row[`${item.key}__observation_date`] ?? "unavailable"),
      displayDate: String(row[`${item.key}__display_date`] ?? row.date ?? "unavailable"),
      sourceName: item.source_name,
      status: item.status
    }];
  });
}

function TooltipCard({
  displayDate,
  entries,
  formatter,
  className = ""
}: {
  displayDate: string;
  entries: TooltipEntry[];
  formatter: (value: number) => string;
  className?: string;
}) {
  return (
    <div className={`rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] p-3 text-xs shadow-xl ${className}`}>
      <p className="font-semibold text-[var(--hc-text)]">Display {displayDate}</p>
      <div className="mt-2 space-y-2">
        {entries.map((entry) => (
          <div key={entry.key} className="border-t border-[var(--hc-border)] pt-2 first:border-0 first:pt-0">
            <p className="font-medium text-[var(--hc-text)]">{entry.label} · {formatter(entry.value)} {entry.unit}</p>
            <p className="mt-1 text-[var(--hc-muted)]">Observation {entry.observationDate} · display {entry.displayDate}</p>
            <p className="text-[var(--hc-muted)]">{entry.sourceName} · {entry.status.toUpperCase()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function downloadCsv(title: string, series: MacroSeries[]) {
  const blob = new Blob([macroSeriesCsv(series)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "macro-series"}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function compactSourceLabel(label: string) {
  return label.replace(/^US ISM\s+/, "").replace(/^China Bloomberg\s+/, "China ");
}

export function MacroChart({ title, description, series, sourceSeries, height = 300, zeroLine = false, valueFormatter }: MacroChartProps) {
  const [view, setView] = useState<"chart" | "table">("chart");
  const [keyboardPointIndex, setKeyboardPointIndex] = useState<number | null>(null);
  const chartInstructionsId = useId();
  const keyboardTooltipId = useId();
  const rows = useMemo(() => mergeSeriesByDate(series), [series]);
  const sourceLinks = useMemo(() => macroSourceLinks(sourceSeries ?? series), [series, sourceSeries]);
  const formatter = valueFormatter ?? ((value: number) => new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(value));
  const firstDate = rows[0]?.date;
  const lastDate = rows.at(-1)?.date;
  const keyboardRow = keyboardPointIndex === null ? null : rows[keyboardPointIndex] ?? null;
  const keyboardEntries = keyboardRow ? tooltipEntriesForRow(keyboardRow, series) : [];
  const keyboardAnnouncement = keyboardRow
    ? `Display ${keyboardRow.date}. ${keyboardEntries.map((entry) => `${entry.label}, ${formatter(entry.value)} ${entry.unit}, observation ${entry.observationDate}, ${entry.status}`).join(". ")}.`
    : "";
  const summary = rows.length
    ? `${title}. ${series.length} series from ${firstDate} through ${lastDate}. Missing observations remain gaps.`
    : `${title}. No validated observations are available.`;

  useEffect(() => {
    setKeyboardPointIndex((current) => {
      if (current === null) return null;
      return rows.length ? Math.min(current, rows.length - 1) : null;
    });
  }, [rows.length]);

  return (
    <div className="min-w-0">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[var(--hc-text)]">{title}</h3>
          {description ? <p className="mt-1 max-w-3xl text-xs leading-5 text-[var(--hc-muted)]">{description}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sourceLinks.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${source.label} source on MacroMicro in a new tab`}
              title={`Open ${source.label} on MacroMicro`}
              data-source-link={source.url}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] px-3 text-xs font-semibold text-[var(--hc-muted)] transition-colors hover:border-[var(--hc-blue)] hover:text-[var(--hc-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hc-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--hc-surface-1)]"
            >
              <ExternalLink size={14} strokeWidth={1.8} aria-hidden="true" />
              <span className="max-w-36 truncate">
                {sourceLinks.length === 1 ? "Open source" : compactSourceLabel(source.label)}
              </span>
            </a>
          ))}
          <div role="group" aria-label={`${title} view`} className="flex rounded-md border border-[var(--hc-border)] bg-[var(--hc-bg)] p-1">
            <button
              type="button"
              aria-pressed={view === "chart"}
              onClick={() => setView("chart")}
              className={`inline-flex min-h-11 items-center gap-1.5 rounded px-3 text-xs font-semibold ${view === "chart" ? "bg-[var(--hc-blue-soft)] text-[var(--hc-text)]" : "text-[var(--hc-muted)]"}`}
            >
              <LineChartIcon size={15} aria-hidden="true" /> Chart
            </button>
            <button
              type="button"
              aria-pressed={view === "table"}
              onClick={() => setView("table")}
              className={`inline-flex min-h-11 items-center gap-1.5 rounded px-3 text-xs font-semibold ${view === "table" ? "bg-[var(--hc-blue-soft)] text-[var(--hc-text)]" : "text-[var(--hc-muted)]"}`}
            >
              <Table2 size={15} aria-hidden="true" /> Table
            </button>
          </div>
          <button
            type="button"
            onClick={() => downloadCsv(title, series)}
            disabled={series.every((item) => item.points.length === 0)}
            className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[var(--hc-border-strong)] px-3 text-xs font-semibold text-[var(--hc-muted)] transition-colors hover:bg-[var(--hc-surface-2)] hover:text-[var(--hc-text)] disabled:cursor-not-allowed disabled:opacity-45"
          >
            <Download size={15} aria-hidden="true" /> CSV
          </button>
        </div>
      </div>

      <p className="sr-only">{summary}</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {series.map((item) => (
          <DataState
            key={item.key}
            status={item.status}
            source={item.source_name}
            observationDate={item.last_observation_date}
            lastSuccessAt={item.last_success_at}
          />
        ))}
      </div>
      {series.length > 1 ? (
        <div aria-label={`${title} series legend`} className="mb-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[var(--hc-muted)]">
          {series.map((item, index) => (
            <div key={item.key} className="inline-flex items-center gap-2">
              <span
                aria-hidden="true"
                className="block w-5 border-t-2"
                style={{
                  borderColor: COLORS[index % COLORS.length],
                  borderTopStyle: DASHES[index % DASHES.length] ? "dashed" : "solid"
                }}
              />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="grid min-h-44 place-items-center rounded-md border border-dashed border-[var(--hc-border-strong)] bg-[var(--hc-bg)] px-4 text-center">
          <div>
            <CircleOffMessage />
            <p className="mt-2 text-sm font-medium text-[var(--hc-text)]">No validated observations</p>
            <p className="mt-1 text-xs text-[var(--hc-muted)]">This panel remains visible until the collector validates the source.</p>
          </div>
        </div>
      ) : view === "table" ? (
        <div className="max-h-[420px] overflow-auto rounded-md border border-[var(--hc-border)]">
          <table className="w-full min-w-[640px] border-collapse text-left text-xs">
            <caption className="sr-only">{summary}</caption>
            <thead className="sticky top-0 bg-[var(--hc-surface-2)] text-[var(--hc-muted)]">
              <tr>
                <th scope="col" className="px-3 py-3 font-semibold">Display date</th>
                {series.map((item) => <th scope="col" key={item.key} className="px-3 py-3 font-semibold">{item.label} ({item.unit})</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.date} className="border-t border-[var(--hc-border)]">
                  <th scope="row" className="whitespace-nowrap px-3 py-2.5 font-medium text-[var(--hc-text)]">{row.date}</th>
                  {series.map((item) => (
                    <td key={item.key} className="px-3 py-2.5 text-[var(--hc-muted)]">
                      {typeof row[item.key] === "number" ? formatter(row[item.key] as number) : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          role="group"
          aria-label={summary}
          aria-roledescription="interactive time-series chart"
          aria-describedby={`${chartInstructionsId}${keyboardRow ? ` ${keyboardTooltipId}` : ""}`}
          aria-keyshortcuts="ArrowLeft ArrowRight Home End Escape"
          data-chart-keyboard={title}
          data-active-date={keyboardRow?.date}
          tabIndex={0}
          className="relative rounded-md"
          style={{ height }}
          onFocus={() => setKeyboardPointIndex((current) => current ?? rows.length - 1)}
          onBlur={() => setKeyboardPointIndex(null)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setKeyboardPointIndex(null);
              return;
            }

            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const current = keyboardPointIndex ?? rows.length - 1;
            if (event.key === "Home") setKeyboardPointIndex(0);
            if (event.key === "End") setKeyboardPointIndex(rows.length - 1);
            if (event.key === "ArrowLeft") setKeyboardPointIndex(Math.max(0, current - 1));
            if (event.key === "ArrowRight") setKeyboardPointIndex(Math.min(rows.length - 1, current + 1));
          }}
        >
          <p id={chartInstructionsId} className="sr-only">
            Use Left and Right Arrow to inspect adjacent display dates, Home and End for the first and latest dates, and Escape to close the data-point tooltip.
          </p>
          {keyboardRow ? (
            <div
              id={keyboardTooltipId}
              role="tooltip"
              aria-live="polite"
              aria-atomic="true"
              data-keyboard-chart-tooltip
              className="pointer-events-none absolute inset-x-2 top-2 z-10 sm:left-auto sm:max-w-xs"
            >
              <span className="sr-only">{keyboardAnnouncement}</span>
              <div aria-hidden="true">
                <TooltipCard displayDate={keyboardRow.date} entries={keyboardEntries} formatter={formatter} />
              </div>
            </div>
          ) : null}
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="#252d3a" strokeOpacity={0.75} vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#9ba6b4", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={28} />
              <YAxis
                tick={{ fill: "#9ba6b4", fontSize: 11 }}
                tickFormatter={(value) => formatter(Number(value))}
                axisLine={false}
                tickLine={false}
                width={76}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload as Record<string, string | number> | undefined;
                  if (!row) return null;
                  return (
                    <TooltipCard
                      displayDate={String(label)}
                      entries={tooltipEntriesForRow(row, series)}
                      formatter={formatter}
                      className="max-w-xs"
                    />
                  );
                }}
              />
              {zeroLine ? <ReferenceLine y={0} stroke="#748092" strokeWidth={1.25} /> : null}
              {keyboardRow ? <ReferenceLine x={keyboardRow.date} stroke="#6b9dff" strokeDasharray="3 3" strokeWidth={1.25} /> : null}
              {series.map((item, index) => (
                <Line
                  key={item.key}
                  type="linear"
                  dataKey={item.key}
                  name={`${item.label} (${DASHES[index % DASHES.length] ? "dashed" : "solid"})`}
                  stroke={COLORS[index % COLORS.length]}
                  strokeDasharray={DASHES[index % DASHES.length]}
                  strokeWidth={2.25}
                  dot={false}
                  activeDot={{ r: 5, stroke: "#f3f5f7", strokeWidth: 1.5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function CircleOffMessage() {
  return <span aria-hidden="true" className="mx-auto block size-7 rounded-full border-2 border-[var(--hc-subtle)] after:mx-auto after:block after:h-0.5 after:w-7 after:translate-y-2.5 after:rotate-45 after:bg-[var(--hc-subtle)]" />;
}
